"use client"

import { createContext, useContext, useReducer, useEffect } from "react"

// Define initial state
const initialState = {
  items: [],
  totalItems: 0,
  totalAmount: 0,
}

// Create context
const CartContext = createContext(initialState)

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case "ADD_TO_CART":
      const existingItemIndex = state.items.findIndex((item) => item.id === action.payload.id)

      if (existingItemIndex > -1) {
        // Item exists, update quantity
        const updatedItems = [...state.items]
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + 1,
        }

        return {
          ...state,
          items: updatedItems,
          totalItems: state.totalItems + 1,
          totalAmount: state.totalAmount + action.payload.price,
        }
      } else {
        // Add new item
        const newItem = { ...action.payload, quantity: 1 }
        return {
          ...state,
          items: [...state.items, newItem],
          totalItems: state.totalItems + 1,
          totalAmount: state.totalAmount + action.payload.price,
        }
      }

    case "REMOVE_FROM_CART":
      const itemToRemove = state.items.find((item) => item.id === action.payload)
      if (!itemToRemove) return state

      const filteredItems = state.items.filter((item) => item.id !== action.payload)

      return {
        ...state,
        items: filteredItems,
        totalItems: state.totalItems - itemToRemove.quantity,
        totalAmount: state.totalAmount - itemToRemove.price * itemToRemove.quantity,
      }

    case "UPDATE_QUANTITY":
      const { id, quantity } = action.payload
      const itemIndex = state.items.findIndex((item) => item.id === id)

      if (itemIndex === -1) return state

      const item = state.items[itemIndex]
      const quantityDiff = quantity - item.quantity

      const updatedItems = [...state.items]
      updatedItems[itemIndex] = { ...item, quantity }

      return {
        ...state,
        items: updatedItems,
        totalItems: state.totalItems + quantityDiff,
        totalAmount: state.totalAmount + item.price * quantityDiff,
      }

    case "CLEAR_CART":
      return initialState

    default:
      return state
  }
}

// Provider component
export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState)

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem("cart")
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart)
        Object.keys(cartReducer(initialState, { type: "" })).forEach((key) => {
          if (parsedCart[key] === undefined) {
            throw new Error("Invalid cart data")
          }
        })

        // Dispatch actions to rebuild the state
        parsedCart.items.forEach((item) => {
          for (let i = 0; i < item.quantity; i++) {
            dispatch({
              type: "ADD_TO_CART",
              payload: { ...item, quantity: 1 },
            })
          }
        })
      } catch (error) {
        console.error("Failed to parse cart from localStorage:", error)
        localStorage.removeItem("cart")
      }
    }
  }, [])

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(state))
  }, [state])

  // Add item to cart
  const addToCart = (product) => {
    dispatch({ type: "ADD_TO_CART", payload: product })
  }

  // Remove item from cart
  const removeFromCart = (productId) => {
    dispatch({ type: "REMOVE_FROM_CART", payload: productId })
  }

  // Update item quantity
  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId)
      return
    }

    dispatch({
      type: "UPDATE_QUANTITY",
      payload: { id: productId, quantity },
    })
  }

  // Clear cart
  const clearCart = () => {
    dispatch({ type: "CLEAR_CART" })
  }

  return (
    <CartContext.Provider
      value={{
        ...state,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}
